/**
 * @fileoverview
 * Permission Service - Manages user permissions
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, catchError, throwError, of } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { API_ENDPOINTS, STORAGE_KEYS } from '../../constants';
import { StorageUtil } from '../../utils';
import { Permission } from '../models';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private http = inject(HttpClient);

  // Signals for reactive state - load from storage on init
  private permissionsSignal = signal<Permission[]>(this.loadPermissionsFromStorage());
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  // Public readonly signals
  readonly permissions = this.permissionsSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();

  // Computed signals
  readonly permissionsByModule = computed(() => {
    const perms = this.permissionsSignal();
    const grouped: Record<string, Permission[]> = {};
    
    perms.forEach(perm => {
      if (!grouped[perm.module]) {
        grouped[perm.module] = [];
      }
      grouped[perm.module].push(perm);
    });
    
    return grouped;
  });

  readonly permissionNames = computed(() => {
    return this.permissionsSignal().map(p => p.name);
  });

  /**
   * Fetch user permissions from backend
   * Backend returns hierarchical structure (parent with nested submodules)
   * We flatten it for easier permission checking
   */
  fetchPermissions(): Observable<Permission[]> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    return this.http.get<Permission[]>(
      `${environment.apiUrl}${API_ENDPOINTS.AUTH.PERMISSIONS}`
    ).pipe(
      tap(permissions => {
        // Flatten hierarchical permissions (parent + submodules) into a flat array
        const flattenedPermissions = this.flattenPermissions(permissions);
        this.permissionsSignal.set(flattenedPermissions);
        this.savePermissionsToStorage(flattenedPermissions);
        this.loadingSignal.set(false);
      }),
      catchError(error => {
        // Use standardized error format (extracted by error interceptor)
        const errorMessage = error.errorMessage || error.error?.message || 'Failed to fetch permissions';
        this.errorSignal.set(errorMessage);
        this.loadingSignal.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Flatten hierarchical permissions into a flat array
   * Includes both parent permissions and their submodules
   */
  private flattenPermissions(permissions: Permission[]): Permission[] {
    const flattened: Permission[] = [];
    
    permissions.forEach(perm => {
      // Add parent permission
      flattened.push({
        id: perm.id,
        name: perm.name,
        module: perm.module,
        action: perm.action,
        description: perm.description,
        isSubmodule: false,
        parentModuleId: null,
        submoduleName: null,
      });
      
      // Add submodules if they exist
      if (perm.submodules && perm.submodules.length > 0) {
        perm.submodules.forEach(submodule => {
          flattened.push({
            id: submodule.id,
            name: submodule.name,
            module: submodule.module,
            action: submodule.action,
            description: submodule.description,
            isSubmodule: true,
            parentModuleId: submodule.parentModuleId ?? perm.id,
            submoduleName: submodule.submoduleName ?? null,
          });
        });
      }
    });
    
    return flattened;
  }

  /**
   * Check if user has a specific permission
   * @param permission - Permission name (e.g., 'users.create') or module.action format
   */
  hasPermission(permission: string): boolean {
    const perms = this.permissionsSignal();
    
    // Check by full name
    if (perms.some(p => p.name === permission)) {
      return true;
    }

    // Check by module.action format
    const [module, action] = permission.split('.');
    if (module && action) {
      return perms.some(p => p.module === module && p.action === action);
    }

    return false;
  }

  /**
   * Check if user has any of the specified permissions
   */
  hasAnyPermission(permissions: string[]): boolean {
    return permissions.some(perm => this.hasPermission(perm));
  }

  /**
   * Check if user has all of the specified permissions
   */
  hasAllPermissions(permissions: string[]): boolean {
    return permissions.every(perm => this.hasPermission(perm));
  }

  /**
   * Check if user has permission for a specific module
   */
  hasModulePermission(module: string, action?: string): boolean {
    const perms = this.permissionsSignal();
    
    if (action) {
      return perms.some(p => p.module === module && p.action === action);
    }
    
    return perms.some(p => p.module === module);
  }

  /**
   * Get permissions for a specific module
   */
  getModulePermissions(module: string): Permission[] {
    return this.permissionsSignal().filter(p => p.module === module);
  }

  /**
   * Clear permissions (on logout)
   */
  clearPermissions(): void {
    this.permissionsSignal.set([]);
    this.errorSignal.set(null);
    StorageUtil.removeLocal(STORAGE_KEYS.PERMISSIONS);
  }

  /**
   * Refresh permissions from backend
   */
  refreshPermissions(): Observable<Permission[]> {
    return this.fetchPermissions();
  }

  /**
   * Save permissions to local storage
   */
  private savePermissionsToStorage(permissions: Permission[]): void {
    StorageUtil.setLocal(STORAGE_KEYS.PERMISSIONS, permissions);
  }

  /**
   * Load permissions from local storage
   */
  private loadPermissionsFromStorage(): Permission[] {
    return StorageUtil.getLocal<Permission[]>(STORAGE_KEYS.PERMISSIONS) || [];
  }
}

